/**
 * URL-sync helper for deep-linkable, filter-driven pages.
 *
 * Minimal port of hdpp's `section-navigation.js` (URL writer + reader only, no
 * TomSelect/section machinery). The page compilers in `ui-compilers.js` consume
 * these functions to mirror server-resolved form state into the browser URL.
 */

/**
 * Read the given form field values into a `name -> value` map.
 *
 * Only the requested `keys` are read. Empty values (`''`) and the unset/placeholder
 * value (`'0'`) are dropped so bookmarkable URLs never carry meaningless
 * `runId=0`/`solutionId=0` noise.
 *
 * @param {HTMLFormElement|null|undefined} form The form to read fields from.
 * @param {string[]} keys The field names to read.
 * @returns {Object<string, string>} A map of field name to trimmed value.
 */
export function readFormParams(form, keys) {
  const params = {}
  if (!form) {
    return params
  }
  keys.forEach((key) => {
    const field = form.querySelector(`[name="${key}"]`)
    const value = field?.value?.trim()
    if (value && value !== '0') {
      params[key] = value
    }
  })
  return params
}

/**
 * Write the given query parameters into the current browser URL and history.
 *
 * Each entry is set on a fresh `URL(window.location.href)`; `null`/empty values are
 * deleted. By default the change replaces the current history entry
 * (`history.replaceState`); pass `replace: false` to push a new entry
 * (`history.pushState`), e.g. for a distinct solution pick.
 *
 * @param {{params?: Object<string, *>, replace?: boolean}} [options]
 * @param {Object<string, *>} [options.params] Query parameters to set or delete.
 * @param {boolean} [options.replace=true] Replace (true) or push (false) the history entry.
 */
export function updateUrlState({ params = {}, replace = true } = {}) {
  const url = new URL(window.location.href)
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') {
      url.searchParams.delete(key)
    } else {
      url.searchParams.set(key, String(value))
    }
  })
  window.history[replace ? 'replaceState' : 'pushState']({}, '', url)
}

export default { readFormParams, updateUrlState }
